import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE, HERO_IMAGE_ALT } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const negatives = ['This is not a generic heavy machinery ebook.', 'This is not a collection of random technical notes.', 'This is not another text-heavy manual that takes hours to transform into a lesson.', 'And it is not a disconnected collection of materials you have to organize yourself.'];
const teachingFlow = ['OPEN THE ATLAS.', 'CHOOSE THE MACHINE.', 'SHOW THE DIAGRAM.', 'EXPLAIN THE CONCEPT.', 'RUN THE ACTIVITY.', 'ASSESS THE LEARNING.'];
const specializations = [{
  number: '01',
  step: 'FORKLIFTS & MATERIAL HANDLING',
  detail: 'Understand forklift anatomy, equipment components, lifting systems, load stability awareness and warehouse safety.'
}, {
  number: '02',
  step: 'EXCAVATORS & EARTHMOVING',
  detail: 'Explore excavator anatomy, hydraulic components, attachments, ground conditions and excavation-site hazards.'
}, {
  number: '03',
  step: 'BULLDOZERS, LOADERS & GRADERS',
  detail: 'Teach the differences between major earthmoving machines, their mechanical structures and workplace applications.'
}, {
  number: '04',
  step: 'CRANES & LIFTING EQUIPMENT',
  detail: 'Explain crane components, lifting-system awareness, rigging equipment identification and lifting-site hazards.'
}, {
  number: '05',
  step: 'AERIAL LIFTS & WORK PLATFORMS',
  detail: 'Teach aerial platform anatomy, equipment differences, structural components and elevated-workplace hazard awareness.'
}, {
  number: '06',
  step: 'ROAD CONSTRUCTION & COMPACTION',
  detail: 'Explore road rollers, compactors, asphalt pavers, equipment characteristics and roadwork safety scenarios.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-yellow-50 relative overflow-hidden" data-id="element-283" data-mp="2hpe9dpjnzogx">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-amber-500" data-id="element-284" data-mp="2crcpx3ibtn4x" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-285" data-mp="2qi94vrjby5ti">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-286" data-mp="2mhxoiqy02rnu">
          <p className="text-sm md:text-base font-semibold text-neutral-900/80 mb-4" data-id="element-287" data-mp="1ttjm7yzvp96h">
            The Complete Visual Heavy Equipment Training System for Professional Instructors
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-neutral-900" data-id="element-288" data-mp="22tyb18c1x6eq">
            HEAVY EQUIPMENT INSTRUCTOR <span className="text-amber-600" data-id="element-289" data-mp="3j7f17pvehdz1">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-neutral-900 font-semibold mb-6" data-id="element-290" data-mp="3e952x6edpx97">
            A Complete 6-IN-1 Visual Training & Reference System
          </p>
          <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} className="w-full max-w-sm h-auto mx-auto mb-6 drop-shadow-xl" loading="lazy" data-id="element-291" data-mp="3mokn1w3yqhmg" />
          <p className="text-base md:text-lg text-neutral-800/75 mb-10" data-id="element-292" data-mp="3ctiv6siaz1g6">
            6 PROFESSIONAL VOLUMES · SIX MACHINERY SPECIALIZATIONS · 5 EXCLUSIVE INSTRUCTOR BONUSES
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-293" data-mp="9br6bmzgqxm7">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-neutral-300" data-id="element-294" data-mp="ealp4oxfkm6b">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-295" data-mp="s80ig4qt1e3k">
                <X className="text-neutral-900/50 shrink-0 mt-1" size={18} data-id="element-296" data-mp="x1ljk5np2o6z" />
                <p className="text-neutral-800/80 text-sm" data-id="element-297" data-mp="34kn5k720vogn">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-amber-500" data-id="element-298" data-mp="30h8e8k8mvlnw">
            <div className="flex items-start gap-3" data-id="element-299" data-mp="3o72pixdb1261">
              <Check className="text-amber-600 shrink-0 mt-1" size={20} data-id="element-300" data-mp="3j9b0qc7yhw62" />
              <div className="space-y-3" data-id="element-301" data-mp="3jbdxdf3m6njr">
                <p className="text-neutral-900 font-medium text-base leading-relaxed" data-id="element-302" data-mp="3ei1770jae8ow">
                  HEAVY EQUIPMENT INSTRUCTOR ATLAS is a complete visual training and reference system
                  designed for professionals responsible for heavy machinery education.
                </p>
                <p className="text-neutral-800 text-sm leading-relaxed" data-id="element-303" data-mp="6295qmxfu94z">
                  Whether you&apos;re preparing new operators, developing experienced workers,
                  organizing industrial training, teaching in a technical academy or delivering
                  professional equipment instruction... the system brings six major machinery
                  specializations together in one organized teaching resource.
                </p>
                <p className="text-neutral-900 font-semibold text-sm leading-relaxed" data-id="element-304" data-mp="s93hg7uzw30w">
                  Instead of starting every week with an empty lesson plan, you have professional
                  equipment diagrams, technical illustrations, workplace scenarios and instructor
                  resources available in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-305" data-mp="1g10u2169qz32">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-306" data-mp="1g1sdkdmld6cz">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-amber-600" data-id="element-307" data-mp="1k09nt61b6wym">
                {line}
              </li>)}
          </ul>
          <p className="mt-8 text-lg md:text-xl font-bold text-neutral-900" data-id="element-308" data-mp="3c56z9t1s4fyi">
            Make Your Next Training Session Easier to Prepare and More Visual to Teach.
          </p>
          <div className="flex justify-center mt-5" data-id="element-309" data-mp="3l4bx0x5io59m">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg py-3 sm:py-4 px-5 sm:px-8" data-id="element-310" data-mp="3l2qxsu89by5f">
              GET MY COMPLETE 6-IN-1 TRAINING SYSTEM NOW!
            </Button>
          </div>
        </div>

        <div className="mt-12" data-id="element-311" data-mp="cmo1tlqibxwx">
          <p className="text-neutral-900/80 text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-312" data-mp="8ngexutsq5nf">
            The System Covers Six Major Machinery Specializations
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-313" data-mp="2ehydry1s36wi">
            {specializations.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-neutral-200" data-id="element-314" data-mp="20a3bnw9rod6t">
                <span className="text-sm font-bold text-amber-700 mb-1" data-id="element-315" data-mp="1qktg26fpscdy">{item.number}</span>
                <span className="text-2xl md:text-3xl font-extrabold tracking-tight leading-tight text-amber-600" data-id="element-316" data-mp="1qiz3bfeljqky">
                  {item.step}
                </span>
                <span className="text-sm text-neutral-800/80 mt-1" data-id="element-317" data-mp="1gl2oacijgbhk">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-neutral-900" data-id="element-318" data-mp="17lxo71rbwa3a">
            SIX SPECIALIZATIONS. SIX COMPLETE ATLASES. ONE PROFESSIONAL TRAINING SYSTEM.
          </p>
        </div>
      </div>
    </section>;
}